import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Switch,
  TextField,
  Typography,
  FormControlLabel,
  Paper,
  Divider,
  IconButton,
  Tooltip,
} from "@material-ui/core";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import { useSelector } from "react-redux";
import LineSettingApi from "api/LineSettingApi";
import EnvironmentUtils from "utils/EnviromentUtils";
import { useTranslation } from "react-i18next";

const LineSetting: React.FC = () => {
  const { t } = useTranslation("setting");
  const companyId = useSelector((state: any) => state.account.staff.companyId);
  const [lineChannelAccessToken, setLineChannelAccessToken] = useState("");
  const [lineChannelSecret, setLineChannelSecret] = useState("");
  const [lineNotifyEnabled, setLineNotifyEnabled] = useState(false);
  const [lineBotBasicId, setLineBotBasicId] = useState("");
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [copyTooltip, setCopyTooltip] = useState<string>(t("line.copy"));

  const webhookUrl = useMemo(() => {
    if (!companyId) return "";
    // EnvironmentUtils.getApiUrl() の戻り値の例:
    //   "https://qa.api.staff.no-tel.com/v1/api/staff" (staff-app の場合)
    // Webhook は staff の下ではなく /v1/api/line/webhook 直下なので、
    // 末尾の "/staff" を削ってから /line/webhook を続ける
    const base = (EnvironmentUtils.getApiUrl?.() || "")
      .replace(/\/$/, "")
      .replace(/\/staff$/, "");
    return `${base}/line/webhook/${companyId}`;
  }, [companyId]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const result = await LineSettingApi.find(companyId);
        if (result) {
          setLineChannelAccessToken(
            (result as any).lineChannelAccessToken || ""
          );
          setLineChannelSecret((result as any).lineChannelSecret || "");
          setLineNotifyEnabled((result as any).lineNotifyEnabled || false);
          setLineBotBasicId((result as any).lineBotBasicId || "");
        }
      } catch (e) {
        console.error(t("line.fetchFailed"), e);
      }
    };
    if (companyId) fetchData();
  }, [companyId, t]);

  const handleSave = async () => {
    setLoading(true);
    setSaved(false);
    try {
      await LineSettingApi.update(companyId, {
        lineChannelAccessToken: lineChannelAccessToken || undefined,
        lineChannelSecret: lineChannelSecret || undefined,
        lineNotifyEnabled,
        lineBotBasicId: lineBotBasicId || undefined,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      alert(t("line.saveFailed"));
    }
    setLoading(false);
  };

  const handleCopyWebhookUrl = async () => {
    try {
      await navigator.clipboard.writeText(webhookUrl);
      setCopyTooltip(t("line.copied"));
      setTimeout(() => setCopyTooltip(t("line.copy")), 1500);
    } catch (e) {
      setCopyTooltip(t("line.copyFailed"));
    }
  };

  return (
    <Box padding={3}>
      <Typography variant="h5" gutterBottom>
        {t("line.title")}
      </Typography>
      <Divider style={{ marginBottom: 20 }} />

      <Paper style={{ padding: 24, maxWidth: 720 }}>
        <FormControlLabel
          control={
            <Switch
              checked={lineNotifyEnabled}
              onChange={(e) => setLineNotifyEnabled(e.target.checked)}
              color="primary"
            />
          }
          label={t("line.enableLabel")}
        />

        <Box marginTop={3}>
          <TextField
            fullWidth
            label={t("line.channelAccessToken")}
            value={lineChannelAccessToken}
            onChange={(e) => setLineChannelAccessToken(e.target.value)}
            variant="outlined"
            margin="normal"
            type="password"
            placeholder={t("line.channelAccessTokenPlaceholder")}
            helperText={t("line.channelAccessTokenHelp")}
          />
        </Box>

        <Box marginTop={1}>
          <TextField
            fullWidth
            label={t("line.channelSecret")}
            value={lineChannelSecret}
            onChange={(e) => setLineChannelSecret(e.target.value)}
            variant="outlined"
            margin="normal"
            type="password"
            placeholder={t("line.channelSecretPlaceholder")}
            helperText={t("line.channelSecretHelp")}
          />
        </Box>

        <Box marginTop={1}>
          <TextField
            fullWidth
            label={t("line.basicId")}
            value={lineBotBasicId}
            onChange={(e) => setLineBotBasicId(e.target.value)}
            variant="outlined"
            margin="normal"
            placeholder="@xxxxxxx"
            helperText={t("line.basicIdHelp")}
          />
        </Box>

        <Box marginTop={2}>
          <Typography variant="subtitle2" gutterBottom>
            {t("line.webhookUrl")}
          </Typography>
          <Box display="flex" alignItems="center">
            <TextField
              fullWidth
              value={webhookUrl}
              variant="outlined"
              size="small"
              InputProps={{ readOnly: true }}
            />
            <Tooltip title={copyTooltip}>
              <IconButton onClick={handleCopyWebhookUrl} size="small">
                <FileCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          <Typography variant="caption" color="textSecondary">
            {t("line.webhookNote")}
          </Typography>
        </Box>

        <Box marginTop={3} display="flex" alignItems="center">
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={loading}
          >
            {loading ? t("line.saving") : t("line.save")}
          </Button>
          {saved && (
            <Typography style={{ marginLeft: 16, color: "green" }}>
              {t("line.saved")}
            </Typography>
          )}
        </Box>
      </Paper>

      <Paper style={{ padding: 24, maxWidth: 720, marginTop: 24 }}>
        <Typography variant="h6" gutterBottom>
          {t("line.setupTitle")}
        </Typography>
        <Typography variant="body2" color="textSecondary" component="div">
          <ol style={{ paddingLeft: 18, lineHeight: 1.8 }}>
            <li>
              <a
                href="https://entry.line.biz/form/entry/unverified"
                target="_blank"
                rel="noopener noreferrer"
              >
                LINE 公式アカウントを作成
              </a>
              （まだお持ちでない場合）
            </li>
            <li>
              LINE Official Account Manager の <strong>設定 &gt; Messaging API</strong> から
              「Messaging APIを利用する」を有効化
            </li>
            <li>
              <a
                href="https://developers.line.biz/console/"
                target="_blank"
                rel="noopener noreferrer"
              >
                LINE Developers コンソール
              </a>
              で該当チャネルを開き、<strong>Messaging API設定</strong> タブから:
              <ul>
                <li>「ボットのベーシックID」(@xxx) を上の欄にコピペ</li>
                <li>「Webhook URL」欄に上のWebhook URLをコピペ → 「Webhookの利用」を ON</li>
                <li>「チャネルアクセストークン（長期）」の「発行」ボタンを押して、表示されたトークンを上の欄にコピペ</li>
              </ul>
            </li>
            <li>
              <strong>チャネル基本設定</strong> タブから「チャネルシークレット」を上の欄にコピペ
            </li>
            <li>
              LINE Official Account Manager の <strong>応答設定</strong> で：
              <ul>
                <li>「応答メッセージ」を <strong>OFF</strong>（オウム返しを止めるため）</li>
                <li>「あいさつメッセージ」は任意（友だち追加時の自動メッセージ）</li>
              </ul>
            </li>
            <li>上の「LINE通知を有効にする」を ON にして保存</li>
            <li>キャスト・ドライバーが各自のアプリで「LINE連携」ボタンから連携</li>
          </ol>
        </Typography>
      </Paper>
    </Box>
  );
};

export default LineSetting;
